import React, { useState, useEffect } from "react";
import { ProcessAutoResult } from "./ProcessAutoQuiz";
import { focusAreas } from "./features";
import {
  AccountTree,
  Speed,
  Sync,
  ArrowForward,
  CheckCircle,
  Warning,
  TrendingUp,
  Info,
} from "@mui/icons-material";

interface ProcessAutoResultsProps {
  results?: ProcessAutoResult[];
  onRestart?: () => void;
}

const ProcessAutoResults: React.FC<ProcessAutoResultsProps> = ({
  results: propResults,
  onRestart,
}) => {
  const [animationStep, setAnimationStep] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Get results from props or localStorage
  const results =
    propResults ||
    JSON.parse(localStorage.getItem("processauto_results") || "[]");

  useEffect(() => {
    const timer = setInterval(() => {
      setAnimationStep((prev) => Math.min(prev + 1, 3));
    }, 300);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleRestart = () => {
    if (onRestart) {
      onRestart();
    } else {
      window.location.href = "/processauto";
    }
  };

  // Calculate total scores
  const totalScores = results.reduce(
    (
      acc: {
        workflowAutomation: number;
        taskEfficiency: number;
        integration: number;
      },
      result: ProcessAutoResult
    ) => ({
      workflowAutomation:
        acc.workflowAutomation + result.scores.workflowAutomation,
      taskEfficiency: acc.taskEfficiency + result.scores.taskEfficiency,
      integration: acc.integration + result.scores.integration,
    }),
    { workflowAutomation: 0, taskEfficiency: 0, integration: 0 }
  );

  // Determine primary focus
  const maxScore = Math.max(
    totalScores.workflowAutomation,
    totalScores.taskEfficiency,
    totalScores.integration
  );

  let primaryFocus = "";
  if (totalScores.workflowAutomation === maxScore) {
    primaryFocus = "workflowAutomation";
  } else if (totalScores.taskEfficiency === maxScore) {
    primaryFocus = "taskEfficiency";
  } else {
    primaryFocus = "integration";
  }

  const focusDetails = focusAreas[primaryFocus];
  const focusColor = "#c9a750";

  const getIcon = (type: string) => {
    switch (type) {
      case "workflowAutomation":
        return <AccountTree style={{ fontSize: "4rem" }} />;
      case "taskEfficiency":
        return <Speed style={{ fontSize: "4rem" }} />;
      case "integration":
        return <Sync style={{ fontSize: "4rem" }} />;
      default:
        return <AccountTree style={{ fontSize: "4rem" }} />;
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #667eea 0%, #152A53 100%)",
        padding: isMobile ? "2rem 1rem" : "3rem 2rem",
      }}
    >
      <div
        style={{
          maxWidth: "1200px",
          margin: "0 auto",
        }}
      >
        {/* Header */}
        <div
          style={{
            textAlign: "center",
            marginBottom: "3rem",
            opacity: animationStep >= 1 ? 1 : 0,
            transform:
              animationStep >= 1 ? "translateY(0)" : "translateY(20px)",
            transition: "all 0.6s ease-out",
          }}
        >
          <h1
            style={{
              fontSize: isMobile ? "2rem" : "3rem",
              fontWeight: "700",
              color: "white",
              marginBottom: "1rem",
            }}
          >
            Your Process Automation Roadmap
          </h1>
          <p
            style={{
              fontSize: isMobile ? "1rem" : "1.25rem",
              color: "rgba(255, 255, 255, 0.9)",
            }}
          >
            Here's how to streamline your operations and eliminate bottlenecks
          </p>
        </div>

        {/* Scores Overview */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)",
            gap: "1.5rem",
            marginBottom: "3rem",
            opacity: animationStep >= 2 ? 1 : 0,
            transform:
              animationStep >= 2 ? "translateY(0)" : "translateY(20px)",
            transition: "all 0.6s ease-out 0.2s",
          }}
        >
          {[
            {
              type: "workflowAutomation",
              label: "Workflow Automation",
              icon: <AccountTree />,
              color: "#c9a750",
            },
            {
              type: "taskEfficiency",
              label: "Task Efficiency",
              icon: <Speed />,
              color: "#c9a750",
            },
            {
              type: "integration",
              label: "System Integration",
              icon: <Sync />,
              color: "#c9a750",
            },
          ].map((item) => (
            <div
              key={item.type}
              style={{
                backgroundColor: "white",
                borderRadius: "16px",
                padding: "2rem",
                boxShadow: "0 10px 30px rgba(0, 0, 0, 0.2)",
                border:
                  item.type === primaryFocus
                    ? `3px solid ${item.color}`
                    : "3px solid transparent",
                position: "relative",
              }}
            >
              {item.type === primaryFocus && (
                <div
                  style={{
                    position: "absolute",
                    top: "-12px",
                    left: "50%",
                    transform: "translateX(-50%)",
                    backgroundColor: item.color,
                    color: "white",
                    padding: "0.5rem 1rem",
                    borderRadius: "20px",
                    fontSize: "0.75rem",
                    fontWeight: "700",
                    textTransform: "uppercase",
                    whiteSpace: "nowrap",
                  }}
                >
                  Start Here
                </div>
              )}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: "1rem",
                  color: item.color,
                }}
              >
                {item.icon}
                <h3
                  style={{
                    fontSize: "1rem",
                    fontWeight: "600",
                    marginLeft: "0.5rem",
                    color: item.type === primaryFocus ? item.color : "#1a202c",
                  }}
                >
                  {item.label}
                </h3>
              </div>
              <div
                style={{
                  fontSize: "2.5rem",
                  fontWeight: "700",
                  color: item.color,
                  marginBottom: "0.5rem",
                }}
              >
                {item.type === "workflowAutomation"
                  ? totalScores.workflowAutomation
                  : item.type === "taskEfficiency"
                  ? totalScores.taskEfficiency
                  : totalScores.integration}
              </div>
              <div
                style={{
                  fontSize: "0.875rem",
                  color: "#718096",
                }}
              >
                Priority Score
              </div>
            </div>
          ))}
        </div>

        {/* Primary Focus Details */}
        <div
          style={{
            backgroundColor: "white",
            borderRadius: "20px",
            padding: isMobile ? "2rem" : "3rem",
            marginBottom: "2rem",
            boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
            opacity: animationStep >= 3 ? 1 : 0,
            transform:
              animationStep >= 3 ? "translateY(0)" : "translateY(20px)",
            transition: "all 0.6s ease-out 0.4s",
          }}
        >
          {/* Focus Header */}
          <div
            style={{
              textAlign: "center",
              marginBottom: "3rem",
              padding: "2rem",
              backgroundColor: "#fefce8",
              borderRadius: "16px",
            }}
          >
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: "80px",
                height: "80px",
                borderRadius: "50%",
                backgroundColor: focusColor,
                color: "white",
                marginBottom: "1rem",
              }}
            >
              {getIcon(primaryFocus)}
            </div>
            <h2
              style={{
                fontSize: isMobile ? "1.75rem" : "2.5rem",
                fontWeight: "700",
                color: focusColor,
                marginBottom: "0.5rem",
              }}
            >
              {focusDetails.title}
            </h2>
            <p
              style={{
                fontSize: "1.25rem",
                color: "#4a5568",
                fontWeight: "600",
              }}
            >
              Your Primary Focus Area
            </p>
          </div>

          {/* Description */}
          <div style={{ marginBottom: "3rem" }}>
            <p
              style={{
                fontSize: "1.125rem",
                lineHeight: "1.8",
                color: "#2d3748",
              }}
            >
              {focusDetails.description}
            </p>
          </div>

          {/* Card with 2x2 Grid */}
          <div
            style={{
              backgroundColor: "#f8fafc",
              borderRadius: "16px",
              padding: isMobile ? "1.5rem" : "2rem",
              marginBottom: "3rem",
              boxShadow: "0 10px 30px rgba(0, 0, 0, 0.1)",
            }}
          >
            {/* 2x2 Grid for Symptoms and Impacts */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                gap: "2rem",
                marginBottom: "2rem",
              }}
            >
              {/* Current State */}
              <div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: "1rem",
                  }}
                >
                  <Warning
                    style={{
                      color: "#f59e0b",
                      fontSize: "1.5rem",
                      marginRight: "0.5rem",
                    }}
                  />
                  <h3
                    style={{
                      fontSize: "1.25rem",
                      fontWeight: "700",
                      color: "#1a202c",
                    }}
                  >
                    Current State
                  </h3>
                </div>
                <div>
                  {focusDetails.symptoms.map(
                    (symptom: string, index: number) => (
                      <div
                        key={index}
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          marginBottom: "0.75rem",
                        }}
                      >
                        <span
                          style={{
                            color: focusColor,
                            marginRight: "0.75rem",
                            fontWeight: "700",
                          }}
                        >
                          •
                        </span>
                        <span
                          style={{
                            fontSize: "0.95rem",
                            lineHeight: "1.6",
                            color: "#4a5568",
                          }}
                        >
                          {symptom}
                        </span>
                      </div>
                    )
                  )}
                </div>
              </div>

              {/* Business Impact */}
              <div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: "1rem",
                  }}
                >
                  <TrendingUp
                    style={{
                      color: "#ef4444",
                      fontSize: "1.5rem",
                      marginRight: "0.5rem",
                    }}
                  />
                  <h3
                    style={{
                      fontSize: "1.25rem",
                      fontWeight: "700",
                      color: "#1a202c",
                    }}
                  >
                    Business Impact
                  </h3>
                </div>
                <div>
                  {focusDetails.impacts.map((impact: string, index: number) => (
                    <div
                      key={index}
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        marginBottom: "0.75rem",
                      }}
                    >
                      <span
                        style={{
                          color: "#ef4444",
                          marginRight: "0.75rem",
                          fontWeight: "700",
                        }}
                      >
                        •
                      </span>
                      <span
                        style={{
                          fontSize: "0.95rem",
                          lineHeight: "1.6",
                          color: "#4a5568",
                        }}
                      >
                        {impact}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Recommended Solutions - Full Width */}
            <div
              style={{
                borderTop: "1px solid #e2e8f0",
                paddingTop: "2rem",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: "1rem",
                }}
              >
                <CheckCircle
                  style={{
                    color: focusColor,
                    fontSize: "1.5rem",
                    marginRight: "0.5rem",
                  }}
                />
                <h3
                  style={{
                    fontSize: "1.25rem",
                    fontWeight: "700",
                    color: "#1a202c",
                  }}
                >
                  Strategic Solutions
                </h3>
              </div>
              <div>
                {focusDetails.solutions.map(
                  (solution: string, index: number) => (
                    <div
                      key={index}
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        marginBottom: "0.75rem",
                      }}
                    >
                      <span
                        style={{
                          color: focusColor,
                          marginRight: "0.75rem",
                          fontWeight: "700",
                        }}
                      >
                        {index + 1}.
                      </span>
                      <span
                        style={{
                          fontSize: "0.95rem",
                          lineHeight: "1.6",
                          color: "#4a5568",
                        }}
                      >
                        {solution}
                      </span>
                    </div>
                  )
                )}
              </div>
            </div>
          </div>

          {/* Next Steps */}
          <div
            style={{
              backgroundColor: "#fefce8",
              borderRadius: "16px",
              padding: "2rem",
              marginBottom: "2rem",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: "1rem",
              }}
            >
              <ArrowForward
                style={{
                  color: focusColor,
                  fontSize: "1.5rem",
                  marginRight: "0.5rem",
                }}
              />
              <h3
                style={{
                  fontSize: "1.5rem",
                  fontWeight: "700",
                  color: "#1a202c",
                }}
              >
                Your Action Plan
              </h3>
            </div>
            <div style={{ paddingLeft: "2rem" }}>
              {focusDetails.nextSteps.map((step: any, index: number) => (
                <div
                  key={index}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    marginBottom: "1.5rem",
                  }}
                >
                  <div
                    style={{
                      width: "32px",
                      height: "32px",
                      borderRadius: "50%",
                      backgroundColor: focusColor,
                      color: "white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "0.875rem",
                      fontWeight: "700",
                      flexShrink: 0,
                      marginRight: "0.75rem",
                    }}
                  >
                    {index + 1}
                  </div>
                  <div>
                    <div
                      style={{
                        fontSize: "1.125rem",
                        fontWeight: "600",
                        color: "#1a202c",
                        marginBottom: "0.25rem",
                      }}
                    >
                      {step.step}
                    </div>
                    <p
                      style={{
                        fontSize: "1rem",
                        lineHeight: "1.6",
                        color: "#2d3748",
                        margin: 0,
                      }}
                    >
                      {step.action}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Key Insight */}
          <div
            style={{
              backgroundColor: "#fefce8",
              borderRadius: "16px",
              padding: "2rem",
              border: `2px solid ${focusColor}`,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: "1rem",
              }}
            >
              <Info
                style={{
                  color: focusColor,
                  fontSize: "1.5rem",
                  marginRight: "0.5rem",
                }}
              />
              <h3
                style={{
                  fontSize: "1.25rem",
                  fontWeight: "700",
                  color: "#1a202c",
                }}
              >
                Key Insight
              </h3>
            </div>
            <p
              style={{
                fontSize: "1.125rem",
                color: "#1a202c",
                lineHeight: "1.8",
                fontStyle: "italic",
                margin: 0,
              }}
            >
              "{focusDetails.insight}"
            </p>
          </div>
        </div>

        {/* Call to Action */}
        <div
          style={{
            backgroundColor: "white",
            borderRadius: "20px",
            padding: isMobile ? "2rem" : "3rem",
            textAlign: "center",
            marginBottom: "2rem",
            boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
          }}
        >
          <h3
            style={{
              fontSize: isMobile ? "1.5rem" : "2rem",
              fontWeight: "700",
              color: "#1a202c",
              marginBottom: "1rem",
            }}
          >
            Ready to Automate Your Operations?
          </h3>
          <p
            style={{
              fontSize: "1.125rem",
              color: "#4a5568",
              marginBottom: "2rem",
              lineHeight: "1.6",
            }}
          >
            Get expert guidance on streamlining your business processes and
            eliminating bottlenecks.
          </p>
          <div
            style={{
              display: "flex",
              gap: "1rem",
              justifyContent: "center",
              flexWrap: "wrap",
            }}
          >
            <a
              href="https://intheraconsultinggroup.com/3-day-business-audit-questionnaires"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                padding: "1rem 2rem",
                fontSize: "1.1rem",
                fontWeight: "600",
                color: "white",
                background: focusColor,
                border: "none",
                borderRadius: "12px",
                cursor: "pointer",
                textDecoration: "none",
                display: "inline-flex",
                alignItems: "center",
                gap: "0.5rem",
                boxShadow: "0 4px 15px rgba(201, 167, 80, 0.4)",
                transition: "all 0.2s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow =
                  "0 6px 20px rgba(201, 167, 80, 0.5)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow =
                  "0 4px 15px rgba(201, 167, 80, 0.4)";
              }}
            >
              Schedule Free Audit
              <ArrowForward />
            </a>
            <button
              onClick={handleRestart}
              style={{
                padding: "1rem 2rem",
                fontSize: "1.1rem",
                fontWeight: "600",
                color: focusColor,
                background: "white",
                border: `2px solid ${focusColor}`,
                borderRadius: "12px",
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: "0.5rem",
                transition: "all 0.2s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#fefce8";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "white";
              }}
            >
              Retake Assessment
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProcessAutoResults;
